import isEmpty from 'lodash/isEmpty.js';

export const defaultMenu = {
    primary: [],
    curriculum: {
        misc_pre: [],
        weeks: { past: [], present: [], future: [] },
        misc_post: [],
    },
    meta: [],
};

export default {
    state: {
        hidden: true,

        dynamicMenu: { ...defaultMenu },

        mobile_nav_open: false,

        staticMenu: null,

        sidebar: null,

        sidebarExpandedRoute: null,
    },

    getters: {
        hasMenu(state) {
            if (state.sidebar) return true;
            if (state.sidebar === 'course-template' && state.dynamicMenu.curriculum.misc_pre) return true;
            if (state.sidebar === 'reports-sidebar') return true;
            if (state.sidebar === 'inbox-sidebar') return true;

            return Object.values(state.dynamicMenu).some(menuSection => {
                if (Array.isArray(menuSection)) {
                    return !isEmpty(menuSection);
                }

                return Object.values(menuSection).some(subSection => !isEmpty(subSection));
            });
        },

        menu(state) {
            if (state.staticMenu && state.sidebar) {
                return state.staticMenu;
            }

            return state.dynamicMenu;
        },

        flatCourseMenu(state, getters) {
            if (!getters.menu?.curriculum) return [];

            return [
                ...(getters.menu.curriculum.misc_pre || []),
                ...(getters.menu.curriculum.weeks?.past || []),
                ...(getters.menu.curriculum.weeks?.present || []),
                ...(getters.menu.curriculum.weeks?.future || []),
                ...(getters.menu.curriculum.misc_post || []),
            ];
        },
    },

    actions: {
        setMenu({ commit }, menu) {
            commit('SET_MENU', menu);

            commit('UPDATE_STATE', { field: 'hidden', value: false });
        },

        setMenuForCourse({ dispatch }, data) {
            // Handle different data structures:
            // - Legacy: data.meta.menu
            // - New menu endpoint: data.data.menu (axios response) or data.menu (direct menu data)
            let menuData;

            if (data.data && data.data.menu) {
                // New menu endpoint format (axios response)
                menuData = data.data.menu;
            } else if (data.meta && data.meta.menu) {
                // Legacy format
                menuData = data.meta.menu;
            } else if (data.menu) {
                // Direct menu data
                menuData = data.menu;
            } else {
                // Fallback - assume data is the menu itself
                menuData = data;
            }

            return dispatch('setMenu', menuData);
        },

        setStaticMenu({ commit }, { menu = defaultMenu, sidebar }) {
            commit('UPDATE_STATE', { field: 'staticMenu', value: menu });
            commit('UPDATE_STATE', { field: 'hidden', value: false });
            commit('UPDATE_STATE', { field: 'sidebar', value: sidebar });
        },

        hide({ commit }) {
            commit('UPDATE_STATE', { field: 'hidden', value: true });
        },

        show({ commit }) {
            commit('UPDATE_STATE', { field: 'hidden', value: false });
        },

        setSidebar({ commit }, sidebar) {
            commit('UPDATE_STATE', { field: 'sidebar', value: sidebar });
            commit('UPDATE_STATE', { field: 'hidden', value: false });
        },

        showSidebarExpanded({ commit }, route) {
            commit('UPDATE_STATE', { field: 'sidebarExpandedRoute', value: route });
        },

        toggleMobileNav({ commit, state }) {
            commit('UPDATE_STATE', { field: 'mobile_nav_open', value: !state.mobile_nav_open });
        },

        reset({ commit }) {
            commit('SET_MENU', defaultMenu);

            commit('UPDATE_STATE', { field: 'hidden', value: true });
            commit('UPDATE_STATE', { field: 'staticMenu', value: null });
            commit('UPDATE_STATE', { field: 'sidebar', value: null });
        },
    },

    mutations: {
        SET_MENU(state, menu) {
            if (!menu) {
                console.warn('SET_MENU called with null/undefined menu data');
                return;
            }

            state.dynamicMenu.primary = menu.primary || [];
            state.dynamicMenu.curriculum.misc_pre = menu.curriculum?.misc_pre || [];
            state.dynamicMenu.curriculum.weeks.past = menu.curriculum?.weeks?.past || [];
            state.dynamicMenu.curriculum.weeks.present = menu.curriculum?.weeks?.present || [];
            state.dynamicMenu.curriculum.weeks.future = menu.curriculum?.weeks?.future || [];
            state.dynamicMenu.curriculum.misc_post = menu.curriculum?.misc_post || [];
            state.dynamicMenu.meta = menu.meta || [];
        },

        UPDATE_STATE(state, { field, value }) {
            if (Object.hasOwn(state, field)) {
                state[field] = value;
            }
        },

        MARK_ACTIVITY_COMPLETED(state, { activityId, completed }) {
            const setCompleted = items => {
                if (!Array.isArray(items)) return;

                items.forEach(item => {
                    if (item && typeof item === 'object') {
                        if (item.type === 'activity' && Number(item.id) === Number(activityId)) {
                            item.completed = completed;
                        }

                        if (Array.isArray(item.items)) setCompleted(item.items);
                    }
                });
            };

            const menu = state.dynamicMenu;
            setCompleted(menu.primary);
            setCompleted(menu.curriculum?.misc_pre);
            setCompleted(menu.curriculum?.weeks?.past);
            setCompleted(menu.curriculum?.weeks?.present);
            setCompleted(menu.curriculum?.weeks?.future);
            setCompleted(menu.curriculum?.misc_post);
            setCompleted(menu.meta);

            // After updating the activity, recompute completion flags for parent sections/weeks
            const recomputeSectionCompletion = items => {
                if (!Array.isArray(items)) return false;

                let allCompleted = true;

                items.forEach(item => {
                    if (!item || typeof item !== 'object') return;

                    if (Array.isArray(item.items) && item.items.length) {
                        const childrenAllDone = recomputeSectionCompletion(item.items);

                        if (item.type === 'section') {
                            item.completed = childrenAllDone;
                        }

                        allCompleted = allCompleted && (item.type === 'activity' ? !!item.completed : childrenAllDone);
                    } else if (item.type === 'activity') {
                        allCompleted = allCompleted && !!item.completed;
                    } else if (item.type === 'section') {
                        // Sections with no children are not considered complete
                        item.completed = false;
                        allCompleted = false;
                    }
                });

                return items.length > 0 ? allCompleted : false;
            };

            recomputeSectionCompletion(menu.primary);
            recomputeSectionCompletion(menu.curriculum?.misc_pre);
            recomputeSectionCompletion(menu.curriculum?.weeks?.past);
            recomputeSectionCompletion(menu.curriculum?.weeks?.present);
            recomputeSectionCompletion(menu.curriculum?.weeks?.future);
            recomputeSectionCompletion(menu.curriculum?.misc_post);
            recomputeSectionCompletion(menu.meta);

            // Force reactivity for consumers that depend on the menu root object
            state.dynamicMenu = { ...state.dynamicMenu };
        },
    },
};
